import { useEffect, useMemo } from "react";
import { InputV2 } from "../../../../FormControlsV2";
import { ICoords } from "../../../../../@types/GoogleMaps/ICoord";
import { ReactComponent as NextIcon } from "../../../../../assets/svg/icons/arrowNext.svg";
import * as geolib from "geolib";
import { IMicroArea } from "../../../../../@types/API/IMicroArea";
import { useTranslation } from "react-i18next";

type MicroAreaWithPrefix = IMicroArea & {
  farm?: { initials?: string };
  area?: { initials?: string };
};

interface CreateMicroAreaProps {
  microArea: MicroAreaWithPrefix | undefined;
  suggestedInitials?: string;
  coordsFence: ICoords[];
  index: number;
  useForm: any;
  currentFence: number;
  acreageUsed: Map<number, number>;
  setCurrentFence: (n: number) => void;
}

function polygonHectares(coords: ICoords | undefined) {
  if (!coords?.length) return 0;
  return Number(geolib.getAreaOfPolygon(coords as any) / 10000);
}

export function NewFence({
  microArea,
  suggestedInitials,
  coordsFence: coordsMicroAreas,
  currentFence: currentMicroArea,
  setCurrentFence: setCurrentMicroArea,
  index,
  useForm,
  acreageUsed,
}: CreateMicroAreaProps) {
  const { t } = useTranslation();
  const { setValue, watch, formState: { errors } } = useForm;

  const initialsPath = `microArea[${index}].initials` as const;
  const acreagePath = `microArea[${index}].acreage` as const;

  const initialsValue = watch(initialsPath);
  const acreageValue = watch(acreagePath);

  const erros = useMemo(() => {
    const micro = errors.microArea as
      | Record<number, { initials?: { message?: string }; acreage?: unknown }>
      | undefined;
    if (!micro) return {};
    const row = Array.isArray(micro) ? micro[index] : micro[index];
    return row ? { ...row } : {};
  }, [errors.microArea, index]);

  const initialsPrefix = useMemo(() => {
    const fi = microArea?.farm?.initials;
    const ai = microArea?.area?.initials;
    if (fi == null || ai == null) return "";
    return `${fi}-${ai}-`;
  }, [microArea?.farm?.initials, microArea?.area?.initials]);

  useEffect(() => {
    if (!suggestedInitials) return;
    setValue(initialsPath, suggestedInitials);
  }, [initialsPath, setValue, suggestedInitials]);

  useEffect(() => {
    setValue(acreagePath, polygonHectares(coordsMicroAreas[index]).toFixed(2));
  }, [acreagePath, coordsMicroAreas, index, setValue]);

  useEffect(() => {
    const fromForm = Number(acreageValue);
    const fallback = polygonHectares(coordsMicroAreas[index]);
    acreageUsed.set(
      index + 1,
      Number.isFinite(fromForm) ? fromForm : fallback,
    );
  }, [acreageValue, acreageUsed, coordsMicroAreas, index]);

  const initialsMessage = erros?.initials?.message as string | undefined;
  const acreageMessage =
    typeof erros?.acreage === "object" && erros?.acreage !== null && "message" in erros.acreage
      ? String((erros.acreage as { message?: string }).message)
      : typeof erros?.acreage === "string"
        ? erros.acreage
        : undefined;

  return (
    <div hidden={currentMicroArea != index} style={{ maxWidth: 500 }}>
      <h2 className="formTitle">{`${t("mapTools.fence")} ${coordsMicroAreas.length != 1 ? `(${index + 1}/${coordsMicroAreas.length})` : ""}`}</h2>

      {coordsMicroAreas.length != 1 ? (
        <span className="formSubTitle">
          {t("mapTools.fenceNavigation")}
        </span>
      ) : (
        ""
      )}

      <div className="containerForm">
        <div className="formCreateMicroArea">
          <br />
          <div>
            <label className="labelForm">{t("mapTools.initials")}</label>
            <InputV2
              name={initialsPath}
              label=""
              prefix={initialsPrefix || undefined}
              value={initialsValue != null && initialsValue !== "" ? String(initialsValue) : ""}
              maxLength={4}
              disabled={false}
              error={initialsMessage}
              onChange={(v) =>
                setValue(initialsPath, v, { shouldValidate: true, shouldDirty: true })
              }
            />
          </div>

          <div>
            <label className="labelForm">{t("mapTools.usefulAreaQuestion")}</label>
            <div className="hectares__container">
              <InputV2
                name={acreagePath}
                label=""
                type="number"
                suffix="ha"
                value={acreageValue != null && acreageValue !== "" ? String(acreageValue) : ""}
                disabled={false}
                error={acreageMessage}
                onChange={(v) =>
                  setValue(acreagePath, v, { shouldValidate: true, shouldDirty: true })
                }
              />
            </div>
          </div>
        </div>

        {coordsMicroAreas.length > 1 && (
          <div className="currentMicroAreaContainer">
            <div className="backMicroArea">
              {currentMicroArea != 0 && (
                <NextIcon
                  onClick={() => {
                    setCurrentMicroArea(currentMicroArea - 1);
                  }}
                />
              )}
            </div>
            <div className="nextMicroArea">
              {currentMicroArea != coordsMicroAreas.length - 1 && (
                <NextIcon
                  onClick={() => {
                    if (currentMicroArea != coordsMicroAreas.length - 1) {
                      setCurrentMicroArea(currentMicroArea + 1);
                    }
                  }}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
